/**
 * 需求变更通知列表的持久化与展示契约。
 *
 * 列表以 JSON 字符串数组存于 localStorage：
 * - 有山头（mountain）的项目：`bsp_notify_list_mtn_<mountainId>`，同一山头下所有车型共用一份；
 * - 无山头/主线项目：`bsp_notify_list_<projectId>`，保持旧行为。
 *
 * 每个条目代表「某个车型的一条需求」，键为 `"<projectId>:<reqId>"`：同一 reqId 在不同车型
 * 下是不同条目，各自一行、各自发送自己那份内容，不再按 reqId 折叠。
 *
 * 展示用的条目必须与「当前作用域内真实存在的需求」求交：历史遗留的、或从其他项目带过来的
 * 条目若不在作用域里，既无法被通知，也无法在弹窗中移除 —— 会表现为
 * 「按钮显示 2、列表只有 1 行、删不掉」。因此 `buildNotifyRows` 对解析不到的键
 * 也输出占位行，保证「列表大小 == 弹窗行数」这条不变量恒成立。
 *
 * 整理（丢弃作用域内不存在的条目、展开历史裸 reqId）只有在「本次拉取的成员集合本身是完整的」
 * 前提下才成立。因此成员是否已由服务端权威数据确认是作用域的一等字段
 * `membersResolved`，并由唯一门禁 `canPruneNotifyScope` 结合「拉取快照 == 当前作用域」
 * 判定。任一前提不成立一律不整理 —— 残留条目最坏是占位行、可手动移除，误操作则会丢用户数据。
 */

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;
type ScopeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const notifyStorageKey = (projectId: number) => `bsp_notify_list_${projectId}`;

export const mountainNotifyStorageKey = (mountainId: number) =>
  `bsp_notify_list_mtn_${mountainId}`;

/** 读取持久化的通知列表。非数组 / 坏 JSON / 非字符串元素一律丢弃。 */
export function readNotifyList(storageKey: string, storage?: ReadableStorage): Set<string> {
  if (!storage) return new Set();
  try {
    const saved = storage.getItem(storageKey);
    if (!saved) return new Set();
    const parsed: unknown = JSON.parse(saved);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
    );
  } catch {
    return new Set();
  }
}

/** 写回持久化的通知列表；存储不可用（隐私模式 / 配额满）时不影响当前会话。 */
export function writeNotifyList(
  storageKey: string,
  list: Set<string>,
  storage?: WritableStorage
): void {
  if (!storage) return;
  try {
    storage.setItem(storageKey, JSON.stringify([...list]));
  } catch {
    // 忽略写入失败：界面上仍以内存中的列表为准
  }
}

/**
 * 列表条目的身份是 `(projectId, reqId)`，编码成 `"<projectId>:<reqId>"` 存于原有的
 * `string[]` 中。同一 reqId 可能同时属于多个车型（shared 模式）：只按 reqId 识别会把
 * 跨车型的副本折叠成一行，也无法在发送时区分该发哪个车型的那一份。
 *
 * 历史数据（本次改动前写入）只有裸 reqId，`parseNotifyKey` 将其标记为
 * `projectId === null` 的旧条目，由 `reconcileNotifyList` 在快照完整时展开。
 */
export const scopedNotifyKey = (projectId: number, reqId: string): string =>
  `${projectId}:${reqId}`;

export interface ParsedNotifyKey {
  /** null 表示历史遗留的裸 reqId（尚未绑定车型）。 */
  projectId: number | null;
  reqId: string;
}

/** 解析列表条目键；不是 `"<数字>:<reqId>"` 形状的一律当作历史裸 reqId。 */
export function parseNotifyKey(key: string): ParsedNotifyKey {
  const match = /^(\d+):(.+)$/.exec(key);
  if (!match) return { projectId: null, reqId: key };
  const projectId = Number(match[1]);
  if (!Number.isSafeInteger(projectId)) return { projectId: null, reqId: key };
  return { projectId, reqId: match[2] };
}

/** 列表里是否已有「某车型的某需求」。历史裸 reqId 视为已加入（整理时会展开）。 */
export function hasNotifyEntry(
  list: Set<string>,
  projectId: number,
  reqId: string
): boolean {
  return list.has(scopedNotifyKey(projectId, reqId)) || list.has(reqId);
}

export interface NotifyScopeEntry {
  projectId: number;
  reqId: string;
}

/**
 * 作用域快照完整（`canPruneNotifyScope` 通过）后整理列表：
 * - 已绑定车型的条目：只保留快照里确实存在的 `(projectId, reqId)`；
 * - 历史裸 reqId：展开成快照里所有归属该 reqId 的车型条目；
 * - 两者都不匹配：丢弃（所属车型已无权访问 / 需求已删除）。
 *
 * 没有变化时返回原集合，保持引用稳定，便于 React 跳过写回。
 */
export function reconcileNotifyList(
  list: Set<string>,
  entries: readonly NotifyScopeEntry[]
): Set<string> {
  const known = new Set<string>();
  const ownersByReqId = new Map<string, number[]>();
  for (const { projectId, reqId } of entries) {
    const key = scopedNotifyKey(projectId, reqId);
    if (known.has(key)) continue;
    known.add(key);
    const owners = ownersByReqId.get(reqId);
    if (owners) owners.push(projectId);
    else ownersByReqId.set(reqId, [projectId]);
  }

  const next = new Set<string>();
  for (const key of list) {
    const parsed = parseNotifyKey(key);
    if (parsed.projectId !== null) {
      if (known.has(key)) next.add(key);
      continue;
    }
    for (const projectId of ownersByReqId.get(parsed.reqId) ?? []) {
      next.add(scopedNotifyKey(projectId, parsed.reqId));
    }
  }

  if (next.size === list.size && [...next].every((key) => list.has(key))) return list;
  return next;
}

export interface NotifyScopeInput {
  projectId: number;
  /** 项目所属山头；null/undefined 表示无山头（含主线项目），回落到项目级共享。 */
  mountainId?: number | null;
  /** 同一山头下的其他车型项目 id（可含自身，内部会去重）。 */
  siblingProjectIds?: number[];
  /**
   * 山头成员集合是否已由服务端权威数据（`GET /mountains/` 的 `mountain.projects`）确认。
   * 省略视为未确认：共享列表照常读写，但禁止裁剪。项目级作用域恒视为已确认。
   */
  membersResolved?: boolean;
}

export interface NotifyScope {
  /** 作用域标识，用于判断异步结果是否过期。 */
  key: string;
  storageKey: string;
  /** 迁移前各车型各自的存储键。 */
  legacyKeys: string[];
  /**
   * 迁移台账键：记录每个 legacyKey 下「已经处理过的 reqId」，用于增量合并并
   * 避免用户删掉的条目被旧 key 复活。项目级作用域为 null。
   */
  migrationKey: string | null;
  /** 参与解析与下发的车型项目 id（含自身）。 */
  projectIds: number[];
  /**
   * `projectIds` 是否就是服务端认可的山头成员全集。为 false 时集合可能只是
   * 「首帧已知的子集」，此时按它裁剪会把兄弟车型的条目当成残留删掉。
   */
  membersResolved: boolean;
}

/** 由项目/山头信息推导存储作用域。 */
export function resolveNotifyScope(input: NotifyScopeInput): NotifyScope {
  const { projectId, mountainId, siblingProjectIds = [], membersResolved = false } = input;
  const projectIds = [...new Set([projectId, ...siblingProjectIds])].filter((id) =>
    Number.isFinite(id)
  );

  if (mountainId == null) {
    return {
      key: `proj:${projectId}`,
      storageKey: notifyStorageKey(projectId),
      legacyKeys: [],
      migrationKey: null,
      projectIds,
      // 项目级作用域的下发范围就是自身，无需等待任何成员表。
      membersResolved: true,
    };
  }

  const storageKey = mountainNotifyStorageKey(mountainId);
  return {
    key: `mtn:${mountainId}`,
    storageKey,
    legacyKeys: projectIds.map(notifyStorageKey).filter((key) => key !== storageKey),
    migrationKey: `${storageKey}__migrated`,
    projectIds,
    membersResolved,
  };
}

/**
 * 一次作用域拉取的结果快照。裁剪的正确性只能由「快照本身」证明，
 * 不能从别处的异步状态反推。
 */
export interface NotifyScopeSnapshot {
  /** 拉取时所属作用域。 */
  scopeKey: string;
  /** 拉取时实际请求的车型 id 快照。 */
  projectIds: readonly number[];
  /** 拉取时作用域成员是否已由服务端权威数据确认。 */
  membersResolved: boolean;
  /** 作用域内每个车型是否都请求成功。 */
  complete: boolean;
  /** 本次拉取到的需求全集（只用于判断是否为空）。 */
  requirements: readonly unknown[];
}

/**
 * 唯一的裁剪门禁。四个前提必须同时成立：
 * 1. 快照属于当前作用域（`scopeKey` 一致）；
 * 2. 快照产生时成员集合已由服务端确认（不是首帧只含自身的子集）；
 * 3. 快照里的车型集合与当前作用域完全一致（避免用旧集合的结果裁新作用域）；
 * 4. 全部请求成功且确实拿到需求。
 *
 * 任何一条不成立都不裁剪：残留条目最坏显示为占位行、用户可手动移除；
 * 误裁则直接丢失其他人加入的共享条目，且无法恢复。
 */
export function canPruneNotifyScope(
  snapshot: NotifyScopeSnapshot,
  scope: NotifyScope
): boolean {
  if (snapshot.scopeKey !== scope.key) return false;
  if (!snapshot.membersResolved) return false;
  if (!snapshot.complete) return false;
  if (snapshot.requirements.length === 0) return false;
  if (snapshot.projectIds.length !== scope.projectIds.length) return false;
  const current = new Set(scope.projectIds);
  return snapshot.projectIds.every((id) => current.has(id));
}

/**
 * 迁移台账：`{ 旧项目 key: 已经处理过的 reqId[] }`。
 *
 * 不能只记「这个 key 处理过」：浏览器里缓存的旧 bundle 在迁移之后仍会继续写
 * `bsp_notify_list_<projectId>`，一旦按 key 记账就再也不会读取它，新写入的需求
 * 会被永久滞留（旧版客户端加入 → 换车型看不到）。
 *
 * - 缺省 / 旧格式（已合并 key 的字符串数组）：返回 `{}`，等价于「未记账」，
 *   让仍滞留的旧 key 被合并一次（正是本次修复要救回的数据）；
 * - 解析失败：返回 `null`，本次既不合并也不删除任何旧数据（安全 no-op）。
 */
function readLegacySeen(
  migrationKey: string,
  storage: ScopeStorage
): Record<string, string[]> | null {
  try {
    const raw = storage.getItem(migrationKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const seen: Record<string, string[]> = {};
    for (const [key, ids] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(ids)) continue;
      seen[key] = ids.filter((id): id is string => typeof id === 'string' && id.trim() !== '');
    }
    return seen;
  } catch {
    return null;
  }
}

/**
 * 读取作用域内的通知列表；山头作用域把各车型旧的 `bsp_notify_list_<projectId>`
 * 合并进来，并在合并后删除旧 key。
 *
 * 每次加载都探测旧 key 是否存在，而不是「台账里有就直接短路」：旧版客户端可能
 * 在迁移之后再次写出旧 key。合并按 ID 增量进行 —— 只并入台账里没见过的 ID，
 * 因此用户删掉的条目不会被仍打开的旧版页面重写复活，而旧 key 的新内容仍能救回。
 */
export function loadNotifyScope(scope: NotifyScope, storage?: ScopeStorage): Set<string> {
  if (!storage) return new Set();
  if (!scope.migrationKey) return readNotifyList(scope.storageKey, storage);

  const seen = readLegacySeen(scope.migrationKey, storage);
  if (seen === null) return readNotifyList(scope.storageKey, storage);

  const union = readNotifyList(scope.storageKey, storage);
  let unionChanged = false;
  let seenChanged = false;

  for (const legacyKey of scope.legacyKeys) {
    const legacyIds = readNotifyList(legacyKey, storage);
    // 旧 key 不存在或为空：没有可救回的数据，也不必写台账。
    if (legacyIds.size === 0) continue;

    const known = new Set(seen[legacyKey] ?? []);
    for (const id of legacyIds) {
      if (known.has(id) || union.has(id)) continue;
      union.add(id);
      unionChanged = true;
    }

    const merged = new Set([...known, ...legacyIds]);
    if (merged.size !== known.size) {
      seen[legacyKey] = [...merged];
      seenChanged = true;
    }

    try {
      storage.removeItem(legacyKey);
    } catch {
      // 忽略：删不掉也不影响本次合并结果
    }
  }

  if (unionChanged) writeNotifyList(scope.storageKey, union, storage);
  if (seenChanged) {
    try {
      storage.setItem(scope.migrationKey, JSON.stringify(seen));
    } catch {
      // 忽略：台账写失败最多导致同一批 ID 下次再合并一次，不会丢数据
    }
  }
  return union;
}

/** 写回作用域内的通知列表。 */
export function saveNotifyScope(
  scope: NotifyScope,
  list: Set<string>,
  storage?: WritableStorage
): void {
  writeNotifyList(scope.storageKey, list, storage);
}

/** 清空作用域内的通知列表；迁移台账保留，避免已处理的旧 key 条目被重新合并。 */
export function clearNotifyScope(scope: NotifyScope, storage?: ScopeStorage): void {
  if (!storage) return;
  try {
    storage.removeItem(scope.storageKey);
  } catch {
    // 忽略
  }
}

/** 作用域内某车型的一条需求。 */
export interface NotifyEntry<T> {
  projectId: number;
  requirement: T;
}

export interface NotifyRow<T> {
  /** 列表条目键：`"<projectId>:<reqId>"`，或历史遗留的裸 reqId。 */
  key: string;
  projectId: number;
  reqId: string;
  /** null 表示当前作用域内解析不到该需求（无权访问所属车型 / 需求已删除等）。 */
  requirement: T | null;
}

/**
 * 把持久化列表展开成弹窗行：**每个（车型, 需求）条目一行**。
 * 不同车型的同一 reqId 各自一行，不再按 reqId 折叠；
 * 解析不到的条目输出占位行，因此 `rows.length` 恒等于 `list.size`。
 */
export function buildNotifyRows<T extends { reqId: string }>(
  list: Set<string>,
  entries: readonly NotifyEntry<T>[]
): NotifyRow<T>[] {
  const rows: NotifyRow<T>[] = [];
  const emitted = new Set<string>();

  for (const { projectId, requirement } of entries) {
    const key = scopedNotifyKey(projectId, requirement.reqId);
    if (!list.has(key) || emitted.has(key)) continue;
    emitted.add(key);
    rows.push({ key, projectId, reqId: requirement.reqId, requirement });
  }

  // 兜底：历史裸 reqId、或所属车型/需求已解析不到的条目，仍出一条可移除的占位行。
  const firstByReqId = new Map<string, NotifyEntry<T>>();
  for (const entry of entries) {
    if (!firstByReqId.has(entry.requirement.reqId)) firstByReqId.set(entry.requirement.reqId, entry);
  }
  for (const key of list) {
    if (emitted.has(key)) continue;
    emitted.add(key);
    const parsed = parseNotifyKey(key);
    if (parsed.projectId !== null) {
      rows.push({ key, projectId: parsed.projectId, reqId: parsed.reqId, requirement: null });
      continue;
    }
    const match = firstByReqId.get(parsed.reqId);
    rows.push({
      key,
      projectId: match?.projectId ?? 0,
      reqId: parsed.reqId,
      requirement: match?.requirement ?? null,
    });
  }
  return rows;
}

/**
 * 发送项：每行一条，`projectId` 就是该行需求所属的车型。
 * 不再把同一 reqId 展开到它归属的所有车型 —— 那正是「一次批次发 N 封信」的根因。
 */
export function buildNotifyBatch<T extends { reqId: string }>(
  rows: readonly NotifyRow<T>[]
): NotifyEntry<T>[] {
  const items: NotifyEntry<T>[] = [];
  for (const row of rows) {
    if (row.requirement) items.push({ projectId: row.projectId, requirement: row.requirement });
  }
  return items;
}

export interface NotifyEntryContext {
  /** 当前登录用户类型；undefined 表示尚未加载（保持 fail-open，不隐藏入口）。 */
  userType?: string | null;
  /** 项目需求模式；`shared` 项目复用基础车型的需求，没有自己的需求页。 */
  reqMode?: string | null;
}

/**
 * 「通知列表」入口是否对该车型可用。
 *
 * 只看两件业务事实：谁能用（外部用户不可用）、该车型有没有自己的需求页（shared 复用型没有）。
 * 刻意**不读** `activeTab` 之类的 UI 导航状态 —— 入口属于车型级能力，不是某个 tab 的附件；
 * 早期实现把它挂在 `activeTab !== 'overview'` 上，导致从侧栏直达/刷新进入兄弟车型（首帧停在
 * 总览页）时入口整体消失，用户看到「跨车型不生效」。
 */
export function canUseSharedNotifyList({ userType, reqMode }: NotifyEntryContext): boolean {
  if (userType === 'external') return false;
  if (reqMode === 'shared') return false;
  return true;
}
